// src/components/ConsentManagement.jsx
import React, { useState, useEffect } from 'react';
import { consentService } from '../services/consentService';
import { Shield, Trash2, Plus, Calendar, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import EmptyState from './EmptyState';
import GrantConsentModal from './GrantConsentModal';
import styles from './ConsentManagement.module.css';

const SCOPE_LABELS = {
  'availability:read:freebusy': 'View Free/Busy Times',
  'availability:read:details': 'View Calendar Details',
  'booking:propose': 'Propose Appointments'
};

export default function ConsentManagement({ userId }) {
  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadConsents = async () => {
      try {
        setLoading(true); setError(null);
        const { data, error: fetchError } = await consentService.getConsents('grantor', true);
        if (cancelled) return;
        if (fetchError) throw fetchError;
        setConsents(data || []);
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading consents:', err);
        setError(err.message || 'Failed to load consents');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadConsents();
    return () => { cancelled = true; };
  }, [userId]);

  const loadConsents = async () => {
    try {
      setLoading(true); setError(null);
      const { data, error: fetchError } = await consentService.getConsents('grantor', true);
      if (fetchError) throw fetchError;
      setConsents(data || []);
    } catch (err) {
      console.error('Error loading consents:', err);
      setError(err.message || 'Failed to load consents');
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (consentId) => {
    if (!confirm('Are you sure you want to revoke this access? The business will no longer be able to view your availability.')) return;
    try {
      setRevoking(consentId); setError(null);
      const { error: revokeError } = await consentService.revokeConsent(consentId);
      if (revokeError) throw revokeError;
      setConsents(prev => prev.filter(c => c.id !== consentId));
      setSuccessMessage('Access revoked successfully!');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error revoking consent:', err);
      setError(err.message || 'Failed to revoke consent');
    } finally {
      setRevoking(null);
    }
  };

  const handleGrantSuccess = (newConsent) => {
    setConsents(prev => [newConsent, ...prev]);
    setShowGrantModal(false);
    const businessName = newConsent.grantee?.business_profile?.business_name ||
                        newConsent.grantee?.full_name || 'Business';
    setSuccessMessage(`Access granted to ${businessName} successfully!`);
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const isExpiringSoon = (expiresAt) => {
    if (!expiresAt) return false;
    const daysUntilExpiry = (new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24);
    return daysUntilExpiry > 0 && daysUntilExpiry <= 7;
  };

  if (loading) {
    return (
      <div className={styles.loadingWrap}>
        <LoadingSpinner size="md" text="Loading access permissions..." />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Shield className={styles.headerIcon} size={24} aria-hidden="true" />
          <div>
            <h2 className={styles.headerTitle}>Shared Access</h2>
            <p className={styles.headerSubtitle}>Manage which businesses can view your availability</p>
          </div>
        </div>
        <button onClick={() => setShowGrantModal(true)} className="btn-primary">
          <Plus size={16} aria-hidden="true" />
          Grant Access
        </button>
      </div>

      {/* Success Alert */}
      {successMessage && (
        <div className={styles.alertSuccess}>
          <CheckCircle size={20} className={styles.alertIcon} aria-hidden="true" />
          <div>
            <p className={styles.alertHeading}>Success</p>
            <p className={styles.alertBody}>{successMessage}</p>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className={styles.alertError}>
          <AlertCircle size={20} className={styles.alertIcon} aria-hidden="true" />
          <div>
            <p className={styles.alertHeading}>Error</p>
            <p className={styles.alertBody}>{error}</p>
          </div>
        </div>
      )}

      {/* Consents List */}
      {consents.length === 0 ? (
        <EmptyState
          icon={Shield}
          title="No shared access yet"
          description="You haven't granted any businesses access to your availability. Grant access to allow businesses to see when you're free and propose appointments."
          action={
            <button onClick={() => setShowGrantModal(true)} className="btn-primary">
              Grant Access to Business
            </button>
          }
        />
      ) : (
        <div className={styles.list}>
          {consents.map((consent) => (
            <div key={consent.id} className={styles.consentCard}>
              <div className={styles.consentMain}>
                <div className={styles.consentTop}>
                  <h3 className={styles.consentName}>
                    {consent.grantee?.business_profile?.business_name ||
                     consent.grantee?.full_name || 'Unknown Business'}
                  </h3>
                  {isExpiringSoon(consent.expires_at) && (
                    <span className={styles.expiringBadge}>
                      <Clock size={12} aria-hidden="true" />
                      Expiring Soon
                    </span>
                  )}
                </div>

                {/* Scopes */}
                <div className={styles.scopeList}>
                  {consent.scopes?.map((scope) => (
                    <span key={scope} className={styles.scopeTag}>
                      <CheckCircle size={12} aria-hidden="true" />
                      {SCOPE_LABELS[scope] || scope}
                    </span>
                  ))}
                </div>

                {/* Metadata */}
                <div className={styles.meta}>
                  <span className={styles.metaItem}>
                    <Calendar size={12} aria-hidden="true" />
                    Granted {formatDate(consent.created_at)}
                  </span>
                  {consent.expires_at && (
                    <span className={styles.metaItem}>
                      <Clock size={12} aria-hidden="true" />
                      Expires {formatDate(consent.expires_at)}
                    </span>
                  )}
                </div>
              </div>

              <button
                onClick={() => handleRevoke(consent.id)}
                disabled={revoking === consent.id}
                className={styles.revokeBtn}
              >
                <Trash2 size={14} aria-hidden="true" />
                {revoking === consent.id ? 'Revoking...' : 'Revoke'}
              </button>
            </div>
          ))}
        </div>
      )}

      {showGrantModal && (
        <GrantConsentModal
          onClose={() => setShowGrantModal(false)}
          onSuccess={handleGrantSuccess}
        />
      )}
    </div>
  );
}
